/* HelloCues overhaul — behaviour on top of Concept.
   Kept in its own file so the work stays separable from the theme's own JS and
   survives a Concept update. Loaded deferred, after theme.js.

   Everything here registers *autonomous* custom elements. Concept itself leans
   heavily on customized built-ins (is="hover-link" and friends), which Safari
   has never implemented — fine for the hover decoration Concept uses them for,
   but not for anything a shopper needs in order to buy. */

(() => {
  'use strict';

  /* ------------------------------------------------------------------ scents
     The Beard Oil is three separate products, one per scent, rather than one
     product with three variants — so this swaps whole products, and cannot use
     the theme's variant picker.

     Updates every [data-scent-link] in the step (the media link, the title link
     and the call to action) plus the image, without a page reload. */
  class RoutineScentPicker extends HTMLElement {
    connectedCallback() {
      this.step = this.closest('[data-scent-step]');
      if (!this.step) return;

      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!input.matches('.routine-step__scent-input')) return;
        this.select(input);
      });
    }

    select(input) {
      const url = input.value;
      if (url) {
        this.step.querySelectorAll('[data-scent-link]').forEach((link) => {
          link.href = url;
        });
      }

      const img = this.step.querySelector('[data-scent-image]');
      if (!img) return;

      const src = input.dataset.image;
      const srcset = input.dataset.srcset;
      const alt = input.dataset.alt;

      // Setting srcset before src avoids a flash of the old bitmap: the browser
      // re-runs selection from the candidate list rather than painting src first.
      if (srcset) img.srcset = srcset;
      if (src) img.src = src;
      if (alt) img.alt = alt;
    }
  }

  if (!customElements.get('routine-scent-picker')) {
    customElements.define('routine-scent-picker', RoutineScentPicker);
  }

  /* -------------------------------------------------- variant-aware panels
     Concept re-fetches the whole product section when the variant changes, but
     only swaps a fixed list of ids into the page — Price, StickyPrice, Sku,
     Inventory, Volume, PricePerItem, BackInStock and the quantity rules. Any
     other block keeps the first variant's markup.

     That is why the Beard Care Set showed Signature's copy on every scent: the
     PDP panels are metafield-driven, and metafields on the *product* cannot
     vary by variant at all. The Set's scent notes now live on each variant, and
     this swaps the panels marked [data-variant-content] out of the HTML Concept
     has already fetched — so there is no second network request and no risk of
     the two responses disagreeing.

     Limitation worth knowing: this swaps a panel's contents, not its existence.
     A panel that renders for one variant and not another would go stale rather
     than disappear. Every Set variant carries notes, so that case does not
     arise today; if it ever does, the whole <details> needs replacing instead. */
  /* ------------------------------------------------------- complete routine
     "Shop the complete routine" was a plain link, and to three different
     destinations across the three templates that carry this module. The client
     asked for one action that adds the selected Beard Oil, the cream and the
     kit together.

     The variants come from the steps themselves rather than a separate product
     list, so the button always matches what is displayed above it — including
     the scent the shopper has actually picked. Adding a step to the module in
     the theme editor adds it to the routine automatically.

     Uses the same /cart/add.js items[] call and the same cartUpdate publish +
     drawer show that Concept's own ProductBundleForm uses, so the cart drawer
     refreshes exactly as it does everywhere else. */
  class RoutineBundle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-routine-bundle-submit]');
      this.errorEl = this.querySelector('[data-routine-bundle-error]');
      this.section = this.closest('.shopify-section, [id^="shopify-section"]') || document;
      this.button?.addEventListener('click', () => this.submit());
    }

    get steps() {
      return Array.from(this.section.querySelectorAll('.routine-step[data-step-variant]'));
    }

    // A step showing scents resolves to whichever scent is currently selected;
    // any other step resolves to its own variant.
    itemsForCart() {
      const items = [];
      for (const step of this.steps) {
        const checked = step.querySelector('.routine-step__scent-input:checked');
        const id = checked?.dataset.variantId || step.dataset.stepVariant;
        if (!id) continue;
        const existing = items.find((i) => i.id === id);
        if (existing) existing.quantity += 1;
        else items.push({ id, quantity: 1 });
      }
      return items;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.toggleAttribute('hidden', !message);
    }

    async submit() {
      const items = this.itemsForCart();
      this.showError('');

      if (!items.length) {
        this.showError('Nothing to add — no products are set on this routine.');
        return;
      }

      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');

      try {
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
        );

        const response = await fetch(window.theme.routes.cart_add_url, {
          ...window.theme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const parsed = await response.json();

        // cart/add.js reports a failure in the body, not the HTTP status.
        if (parsed.status) {
          this.showError(parsed.description || parsed.message || 'Sorry, that could not be added.');
          return;
        }

        const cart = await (await fetch(window.theme.routes.cart_url, { ...window.theme.utils.fetchConfig() })).json();
        cart.sections = parsed.sections;

        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'routine-bundle',
          cart,
        });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));

        document.querySelector('cart-drawer')?.show(this.button);
      } catch (error) {
        this.showError('Sorry, something went wrong. Please try again.');
        console.error(error);
      } finally {
        this.button.removeAttribute('aria-busy');
        this.button.removeAttribute('aria-disabled');
      }
    }
  }

  if (!customElements.get('routine-bundle')) {
    customElements.define('routine-bundle', RoutineBundle);
  }

  /* ------------------------------------------------------------- Method fit
     "Does this fit your routine?" on the product page (Method Everywhere,
     phase 1). The storefront asks Method about the product being viewed and
     shows Method's answer; it makes no judgment of its own.

     The request is anonymous by design. The brief forbids sending Shopify
     customer identity, Method cookies or an Authorization header from the
     theme, so credentials are omitted and the body carries only the surface,
     the capability and the product's public address — plus, on a product with
     variants, which variant is selected (see below).

     Variants. The Beard Care Set and Beard Care Deluxe are answered per oil:
     Method checks Signature and Balance, returns "unavailable" for Uplift, and
     asks the shopper to "choose a beard oil first" when it is told no variant.
     The API takes that as `variantId` (the Shopify variant id, as a STRING — a
     number is rejected with 422) and `variantTitle`. It does not read
     `?variant=` from the product address, and it rejects any field it does not
     know. Both were established against the live API on 2026-10-02.

     Because the answer depends on the oil, changing the oil clears an answer
     already on screen and puts the prompt back, rather than leaving guidance
     for a different oil beside the newly selected one.

     Nothing is kept. The answer is written into the page and not into
     storage, cart attributes, metafields or analytics — and the answer's link
     is deliberately NOT a [data-method-link], so the campaign forwarder below
     leaves the address exactly as Method sent it.

     The endpoint is hellocuesmethod.com directly. method.hellocues.com answers
     with a redirect, and a browser will not follow a redirect on the CORS
     pre-check a JSON POST triggers, so that address fails from the storefront.

     Anything other than a well-formed answer — a timeout, a network or CORS
     failure, a non-200, an unknown state — lands on the same quiet fallback:
     the "can't check" line plus the Method link. Nothing here can touch Add to
     Cart: the element sits outside the product form and owns no form control. */
  const METHOD_STATES = ['fits', 'not_needed', 'needs_context', 'caution', 'unavailable'];

  // The answer's link comes from another service, so it is only used when it
  // is an https address on a HelloCues or Method host. Otherwise the block
  // falls back to the Method link set in the theme.
  const methodUrl = (value) => {
    try {
      const url = new URL(value);
      const host = url.hostname;
      const known = ['hellocues.com', 'hellocuesmethod.com'].some((d) => host === d || host.endsWith(`.${d}`));
      return url.protocol === 'https:' && known ? url.toString() : null;
    } catch {
      return null;
    }
  };

  const methodText = (value) => (typeof value === 'string' ? value.trim() : '');

  class MethodFit extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;

      this.heading = this.querySelector('[data-method-fit-heading]');
      this.reason = this.querySelector('[data-method-fit-reason]');
      this.button = this.querySelector('[data-method-fit-ask]');
      this.buttonLabel = this.querySelector('[data-method-fit-ask-label]');
      this.link = this.querySelector('[data-method-fit-link]');
      this.linkLabel = this.querySelector('[data-method-fit-link-label]');
      if (!this.heading || !this.reason || !this.button || !this.buttonLabel || !this.link || !this.linkLabel) return;

      this.ready = true;

      // What the block says before it is asked, kept so a change of variant
      // can put it back.
      this.prompt = {
        heading: this.heading.textContent,
        reason: this.reason.textContent,
        reasonHidden: this.reason.hidden,
        label: this.buttonLabel.textContent,
        href: this.link.getAttribute('href'),
        linkLabel: this.linkLabel.textContent,
      };

      // { variantId: title } for a product with real variants; absent otherwise.
      try {
        const json = this.querySelector('[data-method-fit-variants]')?.textContent;
        this.variants = json ? JSON.parse(json) : null;
      } catch {
        this.variants = null;
      }
      this.variantId = this.dataset.variantId || '';

      // The link is the no-JavaScript action. With the script running the
      // button takes over, and the link comes back as the answer's next step.
      this.link.hidden = true;
      this.button.hidden = false;
      this.button.addEventListener('click', () => this.ask());

      // Concept announces every variant change with the product it belongs to.
      const events = window.theme?.pubsub?.PUB_SUB_EVENTS;
      if (this.variants && events?.variantChange && typeof window.theme.pubsub.subscribe === 'function') {
        window.theme.pubsub.subscribe(events.variantChange, ({ data }) => {
          if (String(data?.productId) !== this.dataset.productId || !data?.variant?.id) return;
          const id = String(data.variant.id);
          if (id === this.variantId) return;
          this.variantId = id;
          this.reset();
        });
      }
    }

    // The selected variant, for products that have them. Only an id this
    // product actually carries is ever sent.
    variant() {
      if (!this.variants || !(this.variantId in this.variants)) return {};
      return { variantId: this.variantId, variantTitle: this.variants[this.variantId] };
    }

    // Back to the prompt. Any request still in flight is abandoned: its answer
    // would be about the previous variant.
    reset() {
      this.request = (this.request || 0) + 1;
      this.controller?.abort();
      if (!this.dataset.state) return;

      delete this.dataset.state;
      this.removeAttribute('aria-busy');
      this.busy = false;

      this.heading.textContent = this.prompt.heading;
      this.reason.textContent = this.prompt.reason;
      this.reason.hidden = this.prompt.reasonHidden;
      this.buttonLabel.textContent = this.prompt.label;
      this.button.removeAttribute('aria-disabled');
      this.linkLabel.textContent = this.prompt.linkLabel;
      this.link.setAttribute('href', this.prompt.href);

      this.link.hidden = true;
      this.button.hidden = false;
    }

    async ask() {
      if (this.busy) return;
      this.busy = true;

      this.dataset.state = 'loading';
      this.setAttribute('aria-busy', 'true');
      this.button.setAttribute('aria-disabled', 'true');
      this.buttonLabel.textContent = this.dataset.loadingText || 'Checking with Method…';

      const request = (this.request = (this.request || 0) + 1);
      const controller = (this.controller = new AbortController());
      const timer = setTimeout(() => controller.abort(), Number(this.dataset.timeout) || 15000);
      let answer = null;

      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            surface: 'shopify_pdp',
            capability: 'product_fit',
            productUrl: this.dataset.productUrl,
            ...this.variant(),
          }),
          signal: controller.signal,
        });
        if (response.ok) answer = this.read((await response.json())?.data);
      } catch {
        /* Timeout, network, CORS or malformed JSON: the fallback below. */
      } finally {
        clearTimeout(timer);
      }

      // The variant changed while this was in flight: reset() already put the
      // prompt back, and this answer belongs to the old variant.
      if (request !== this.request) return;

      this.show(answer);
    }

    // Returns the parts the block renders, or null when the response is not an
    // answer this block understands.
    read(data) {
      if (!data || !METHOD_STATES.includes(data.state)) return null;

      const heading = methodText(data.shortGuidance);
      if (!heading) return null;

      const url = methodUrl(data.cta?.url);
      return {
        state: data.state,
        heading,
        reason: methodText(data.reasonSummary),
        label: url ? methodText(data.cta?.label) : '',
        url,
      };
    }

    show(answer) {
      const fallbackLabel = this.dataset.fallbackLabel || 'Ask Method';
      const result = answer || {
        state: 'error',
        heading: this.dataset.errorText || 'Method can’t check this product yet.',
        reason: '',
        label: '',
        url: null,
      };

      // needs_context hands off into Method, where the short questions happen,
      // so its action reads as a continuation rather than a second "Ask Method".
      let label = result.label || fallbackLabel;
      if (result.state === 'needs_context' && this.dataset.continueLabel) label = this.dataset.continueLabel;

      const hadFocus = document.activeElement === this.button;

      this.dataset.state = result.state;
      this.heading.textContent = result.heading;
      this.reason.textContent = result.reason;
      this.reason.hidden = !result.reason;
      this.linkLabel.textContent = label;
      this.link.href = result.url || this.dataset.fallbackUrl;

      this.button.hidden = true;
      this.link.hidden = false;
      this.removeAttribute('aria-busy');

      // Only follow the keyboard: if the shopper has moved on, leave them be.
      if (hadFocus) this.link.focus();
    }
  }

  if (!customElements.get('method-fit')) {
    customElements.define('method-fit', MethodFit);
  }

  /* ------------------------------------------------------ Method attribution
     The playbook requires campaign attribution to survive the handoff to the
     Method site, which is a different domain. The section already attaches
     utm_source/medium/campaign so the visit is credited to the storefront, but
     that alone would relabel *paid* traffic as coming from us: someone who
     arrived from an Instagram ad and then tapped through to Method would lose
     the original source at the boundary.

     So anything already on the current URL wins. Only parameters the page does
     not already carry are left as the section set them. Runs on click rather
     than at load so it cannot be cached into a static page. */
  const forwardCampaignParams = (link) => {
    try {
      const incoming = new URLSearchParams(window.location.search);
      const carried = [...incoming.keys()].filter(
        (k) => k.startsWith('utm_') || k === 'gclid' || k === 'fbclid' || k === 'ttclid'
      );
      if (!carried.length) return;

      const url = new URL(link.href, window.location.origin);
      carried.forEach((k) => url.searchParams.set(k, incoming.get(k)));
      link.href = url.toString();
    } catch {
      /* A malformed href should never stop the link working. */
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.('[data-method-link]');
      if (link) forwardCampaignParams(link);
    },
    true
  );

  const events = window.theme?.pubsub?.PUB_SUB_EVENTS;
  if (events?.variantChange && typeof window.theme.pubsub.subscribe === 'function') {
    window.theme.pubsub.subscribe(events.variantChange, ({ data }) => {
      const parsed = data?.parsedHTML;
      if (!parsed) return;

      document.querySelectorAll('[data-variant-content]').forEach((destination) => {
        const key = destination.getAttribute('data-variant-content');
        // Attribute selector with a quoted value — block ids are Shopify-generated
        // and can contain characters a bare selector would choke on.
        const source = parsed.querySelector(`[data-variant-content="${key}"]`);
        if (source && source.innerHTML !== destination.innerHTML) {
          destination.innerHTML = source.innerHTML;
        }
      });
    });
  }
})();
